import { Box, Stack, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';

import {
  LoginLogomark,
  LogomarkSize,
} from '@/components/brand/Logomark/Logomark';
import { Card } from '@/components/layout/Card/Card';

import { CodeChallengeForm } from './components/CodeChallengeForm';
import { LoginForm } from './components/LoginForm';
import {
  DomainNotAllowedNotification,
  ErrorNotification,
  InvalidTenantNotification,
  LoginAttemptNotification,
  LoginExpiredNotification,
  MismatchedEmailNotification,
  RateLimitedNotification,
  SSOSetupInProgressNotification,
} from './components/LoginNotifications';
import { LoginCodes, MESSAGE_QUERY_PARAM } from './LoginPage.constants';
import {
  getAuthEmailFromCookie,
  getValidatedStatusMessageClient,
} from './LoginPage.utils';

interface LoginPageProps {
  displayName: string;
  ssoEnabled: boolean;
  invalidTenantSubdomain: string | null;
  tenantURL: string;
  initialStatusMessage: LoginCodes | null;
  redirectPath: string | null;
  // initialUserEmail is used to prefill the email input
  initialUserEmail: string | null;
}

export function LoginPage({
  displayName,
  ssoEnabled,
  invalidTenantSubdomain,
  tenantURL,
  initialStatusMessage,
  redirectPath,
  initialUserEmail,
}: LoginPageProps) {
  const theme = useTheme();
  const email = getAuthEmailFromCookie();
  const currentStatusMessage = getValidatedStatusMessageClient();

  // this isn't beautiful, but i'm learning how to handle the client/server interplay with next.
  // this is basically just mapping the query param into a validated state param so the UI updates
  // to match the URL.
  const [loginCode, setLoginCode] = useState<LoginCodes | null>(
    initialStatusMessage
  );
  useEffect(() => {
    if (!currentStatusMessage) return;
    setLoginCode(currentStatusMessage);
  }, [currentStatusMessage, setLoginCode]);

  // this should strip the message query param from the URL and reload the page
  function handleRetryLogin() {
    const url = new URL(window.location.href);
    url.searchParams.delete(MESSAGE_QUERY_PARAM);
    window.history.replaceState({}, window.document.title, url.toString());
    // inform the UI that the state has changed so the login form is shown again
    setLoginCode(null);
  }

  return (
    <Box
      sx={{
        backgroundColor: '#fafafa',
        width: '100%',
        height: '100vh',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Card
        variant="outlined"
        sx={{
          px: 3,
          py: 3,
          width: 450,
          boxShadow: theme.palette.shadows.lg,
        }}
      >
        <Stack spacing={3}>
          <Stack direction="row" justifyContent="center">
            <LoginLogomark
              alt={displayName}
              width="70%"
              style={{
                // the goal here is to have the logo have the correct aspect ratio,
                // but not have it overflow horizontally or take up too much
                // vertical space.
                maxWidth: '100%',
                width: 'auto',
                maxHeight: 100,
              }}
              size={LogomarkSize.Wide}
            />
          </Stack>
          {invalidTenantSubdomain ? (
            <InvalidTenantNotification
              tenantURL={tenantURL}
              subdomain={invalidTenantSubdomain}
            />
          ) : (
            <>
              {loginCode === LoginCodes.LINK_SENT && (
                <LoginAttemptNotification
                  onRetryLogin={handleRetryLogin}
                  email={email}
                />
              )}
              {loginCode === LoginCodes.UNKNOWN_ERROR && (
                <ErrorNotification onRetryLogin={handleRetryLogin} />
              )}
              {loginCode === LoginCodes.EMAIL_NOT_FOUND && (
                <MismatchedEmailNotification
                  email={email}
                  onRetryLogin={handleRetryLogin}
                />
              )}
              {loginCode === LoginCodes.EMAIL_RATE_LIMITED && (
                <RateLimitedNotification onRetryLogin={handleRetryLogin} />
              )}
              {loginCode === LoginCodes.SSO_SETUP_IN_PROGRESS && (
                <SSOSetupInProgressNotification
                  onRetryLogin={handleRetryLogin}
                />
              )}
              {loginCode === LoginCodes.LOGIN_EXPIRED && (
                <LoginExpiredNotification onRetryLogin={handleRetryLogin} />
              )}
              {loginCode === LoginCodes.COLLABORATOR_DOMAIN_NOT_ALLOWED && (
                <DomainNotAllowedNotification onRetryLogin={handleRetryLogin} />
              )}
              {loginCode === LoginCodes.CODE_SENT && (
                <CodeChallengeForm email={email} />
              )}
              {!loginCode && (
                <LoginForm
                  initialUserEmail={initialUserEmail}
                  redirectPath={redirectPath}
                  ssoEnabled={ssoEnabled}
                />
              )}
            </>
          )}
        </Stack>
      </Card>
    </Box>
  );
}
